import { some } from "lodash";
import { FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { BuilderAttributes } from "@/components/sku/builder-attributes";
import { LegitimateAttributes } from "@/components/sku/legitimate-attributes";
import { RedirectAttributes } from "@/components/sku/redirect-attributes";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useUpdateSKUMutation } from "@/data/mutations";
import { LoginOption, SKUPageContent, SKUPageType } from "@/data/types";
import { updateSettings } from "@/lib/cms";
import { SKUFormValues } from "@/lib/forms";
import { canEditSKU } from "@/lib/permissions";
import useStore from "@/state";

const resolveDirtyFields = (
  pageType: SKUPageType,
  fields: Record<string, boolean | boolean[] | undefined>,
): boolean => {
  switch (pageType) {
    case SKUPageType.LEGITIMATE:
      return some([
        fields.login_options,
        fields.header_logo_url,
        fields.theme,
        fields.display_serial_number,
      ]);

    case SKUPageType.BUILDER_IO:
      return some([
        fields.header_logo_url,
        fields.builder_io_url,
        fields.builder_io_api_key,
        fields.login_options,
        fields.display_serial_number,
      ]);

    case SKUPageType.REDIRECT:
      return Boolean(fields.redirect_url);

    default:
      return false;
  }
};

const getAttributes = (
  pageType: SKUPageType,
  availableLoginOptions: LoginOption[],
) => {
  switch (pageType) {
    case SKUPageType.LEGITIMATE:
      return (
        <LegitimateAttributes availableLoginOptions={availableLoginOptions} />
      );
    case SKUPageType.BUILDER_IO:
      return (
        <BuilderAttributes availableLoginOptions={availableLoginOptions} />
      );
    case SKUPageType.REDIRECT:
      return <RedirectAttributes />;
    default:
      return null;
  }
};

type Props = {
  skuId: string;
  pageType: SKUPageType;
  pageContent: SKUPageContent;
  availableLoginOptions: LoginOption[];
};

export const WebpageAttributes: FC<Props> = ({
  skuId,
  pageType,
  pageContent,
  availableLoginOptions,
}) => {
  const { session } = useStore();
  const updateSKUMutation = useUpdateSKUMutation(skuId);

  const form = useFormContext<SKUFormValues>();

  const handleSubmit = async (values: SKUFormValues) => {
    await updateSKUMutation.mutateAsync({
      builder_io_url: values.builder_io_url,
      builder_io_api_key: values.builder_io_api_key,
      login_options: values.login_options,
      redirect_url: values.redirect_url,
      header_logo_url: values.header_logo_url,
      page_content: updateSettings(pageContent, {
        theme: values.theme,
      }),
      display_serial_number: values.display_serial_number,
    });

    form.reset({}, { keepValues: true });
  };

  const hasPermission = useMemo(
    () => canEditSKU(session?.permissions),
    [session?.permissions],
  );

  const isDirty = resolveDirtyFields(pageType, form.formState.dirtyFields);

  const isSubmitEnabled = useMemo(() => {
    const isValid = form.formState.isValid;
    const isPending = updateSKUMutation.isPending;

    return hasPermission && isDirty && isValid && !isPending;
  }, [
    hasPermission,
    isDirty,
    form.formState.isValid,
    updateSKUMutation.isPending,
  ]);

  return (
    <Card>
      <CardHeader>
        <h4 className="text-lg font-medium leading-none">Webpage Settings</h4>
        <p className="text-sm text-muted-foreground">
          Customizations applied to the digital experience
        </p>
      </CardHeader>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <CardContent>
          {getAttributes(pageType, availableLoginOptions)}
        </CardContent>
        <div className="border-t px-6 py-4">
          <Button type="submit" disabled={!isSubmitEnabled}>
            Save
          </Button>
        </div>
      </form>
    </Card>
  );
};
